import { DEFAULT_KEYMAP } from '../input/keymap.js';
import { applyRootSettings } from './apply.js';
import type { SettingsState } from './types.js';

const SETTINGS_KEY = 'blackmarble.settings.v1';

type Listener = (settings: SettingsState) => void;
type Updater = (settings: SettingsState) => SettingsState;

const DEFAULT_SETTINGS: SettingsState = {
  display: {
    fullscreenPreferred: false,
    frameCap: 0,
    renderScale: 1,
    vignetteIntensity: 0.34,
  },
  graphics: {
    filmGrainEnabled: true,
    filmGrainIntensity: 0.035,
    bloomEnabled: true,
    scanlineEnabled: false,
    scanlineIntensity: 0.32,
    assetQuality: 'decimated',
  },
  audio: {
    master: 0.9,
    music: 0.7,
    sfx: 0.9,
    ui: 0.85,
    ambience: 0.85,
    dialogue: 0.9,
    muted: false,
    radioFilter: true,
  },
  controls: {
    keymap: { ...DEFAULT_KEYMAP },
    assistMode: 'toggle',
    boostMode: 'hold',
  },
  gameplay: {
    flightAssistDefault: true,
    hintsEnabled: true,
    verifyBeatSec: 2.4,
    noFailReadingMode: false,
  },
  accessibility: {
    colorblindPalette: 'off',
    colorblindIntensity: 1,
    dyslexiaFont: false,
    reduceMotion: false,
    disableScreenFx: false,
    textScale: 1,
    cardOpacity: 1,
    commsScale: 1,
    voiceAutoAdvance: false,
  },
};

const clone = (settings: SettingsState): SettingsState =>
  JSON.parse(JSON.stringify(settings)) as SettingsState;

// Legacy default for throttleCut was 'Backspace'; the Z binding was lost in the
// keymap refactor. Bump any persisted copy still on the old default to the new
// one without clobbering an intentional user rebind.
const LEGACY_THROTTLE_CUT = 'Backspace';

const migrateLegacyKeymap = (settings: SettingsState): SettingsState => {
  if (settings.controls.keymap.throttleCut !== LEGACY_THROTTLE_CUT) return settings;
  settings.controls.keymap.throttleCut = DEFAULT_KEYMAP.throttleCut;
  return settings;
};

const loadStoredSettings = (): SettingsState => {
  const base = clone(DEFAULT_SETTINGS);
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return base;
    return migrateLegacyKeymap(mergeSettings(base, JSON.parse(raw) as Partial<SettingsState>));
  } catch {
    return base;
  }
};

const mergeSettings = (base: SettingsState, incoming: Partial<SettingsState>): SettingsState => ({
  display: { ...base.display, ...incoming.display },
  graphics: { ...base.graphics, ...incoming.graphics },
  audio: { ...base.audio, ...incoming.audio },
  controls: {
    ...base.controls,
    ...incoming.controls,
    keymap: { ...base.controls.keymap, ...incoming.controls?.keymap },
  },
  gameplay: { ...base.gameplay, ...incoming.gameplay },
  accessibility: { ...base.accessibility, ...incoming.accessibility },
});

class SettingsStore {
  private settings = loadStoredSettings();
  private listeners = new Set<Listener>();

  constructor() {
    applyRootSettings(this.settings);
  }

  get(): SettingsState {
    return this.settings;
  }

  update(updater: Updater): void {
    this.settings = mergeSettings(clone(DEFAULT_SETTINGS), updater(this.settings));
    this.persist();
    applyRootSettings(this.settings);
    for (const listener of this.listeners) listener(this.settings);
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private persist(): void {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(this.settings));
    } catch {
      /* local storage is best effort */
    }
  }
}

export const settingsStore = new SettingsStore();
export const getSettings = (): SettingsState => settingsStore.get();
